import React, { Component } from 'react';
import { ItemProvider, isValidType } from '@platform/template';
import { getGlobalStorage, getSysFieldid } from '@platform/api';
import { dfs, ROW_STATUS, presetColumns } from '../../utils';
import classNames from 'classnames';

import Cell from './Cell.js';

export default function useStore(Table) {
    class StoreTable extends Component{
        timer = null;

        constructor(props) {
            super(props);
            this.state = { hoveredKey: '' };
        }

        render() {
            let { rowProps, hasSuspendedRow } = this.props,
                { hoveredKey } = this.state;
            return <Table 
                {...this.props}
                rowProps={(row, rowIndex) => {
                    let rawProps = rowProps?.(row, rowIndex) ?? {}; 

                    if (hasSuspendedRow) {
                        return {
                            ...rawProps,
                            // 优有些悬浮状态需要保留，依赖事件冒泡，所以没用hover做
                            onMouseEnter: (...rest) => {
                                this.timer = setTimeout(() => {
                                    this.setState({ hoveredKey: row.key });
                                    clearTimeout(this.timer);
                                    this.timer = null;
                                }, 300);
                                rawProps.onMouseEnter?.(...rest);
                            },
                            onMouseLeave: (...rest) => {
                                if (this.timer) {
                                    clearTimeout(this.timer);
                                    this.timer = null;
                                } else {
                                    this.setState({ hoveredKey: '' });
                                }
                                rawProps.onMouseLeave?.(...rest);
                            },
                            className: classNames(rawProps.className, { hovered: hoveredKey === row.key }),
                        };
                    }
                    return rawProps;
                }}
            />;    
        }
    }
    return ItemProvider(StoreTable);
}

useStore.displayName = 'useStore';
useStore.dependence = [];
useStore.hooks = function(){
    this.store.validatedInfo = new Map();
    this.store.componentConfig = {};
    return {
        setConfig: config => {
            this.store.componentConfig = config;
        },
        getConfig: () => {
            return this.store.componentConfig;
        },
        setStatus: status => {
            this.store.status = status;
            if (status === 'browse') {
                this.setActiveCell({ colKey: '', rowKey: '' });
                this.clearValidate();
            }
            this.forceUpdate();
        },
        getStatus: () => {
            return this.store.status || 'browse';
        },
        setCellValue: (rowKey, colKey, { value, shouldUpdate = true, changeStatus = true }) => {
            if (this.store.rowDataMap[rowKey]) {
                let row = this.getRowData(rowKey);
                if (changeStatus && row.status === ROW_STATUS.ORIGIN) {
                    row.status = ROW_STATUS.EDIT;
                }

                // 取消校验状态
                if (value?.value && this.getCellProps(rowKey, colKey, 'invalid')) {
                    this.setCellProps(rowKey, colKey, { invalid: false });
                    this.clearCellValidate(rowKey, colKey);
                }

                this.store.rowDataMap[rowKey].values[colKey] = this._formatCellValue({ ...row.values, [colKey]: value }, colKey);
                shouldUpdate && this.updateComponent(this.itemIDManager.getID({ rowKey, colKey }));

                if (this.getColumnProps(colKey, 'itemtype') === 'number' && this.getColumnProps(colKey, 'istotal')) {
                    this._updateTotal();
                    this.forceUpdate();
                }
            } else {
                console.warn('rowKey输入有误');
            }
        },
        getCellValue: (rowKey, colKey) => {
            return this.store.rowDataMap[rowKey]?.values[colKey] || {}; 
        },
        setCellProps: (rowKey, colKey, props) => {
            if (this.store.cellPropsMap[rowKey]) {
                let currentProps = this.store.cellPropsMap[rowKey][colKey];
                this.store.cellPropsMap[rowKey][colKey] = { ...currentProps, ...props };
            } else {
                this.store.cellPropsMap[rowKey] = { [colKey]: props };
            }

            this.updateComponent(this.itemIDManager.getID({ rowKey, colKey }));
        },
        getCellProps: (rowKey, colKey, prop) => {
            if (prop) {
                return this.store.cellPropsMap[rowKey]?.[colKey]?.[prop] ?? this.store.columnsMap[colKey]?.[prop];
            } 
            // 初始情况时，继承columnsMap
            return { ...this.store.columnsMap[colKey], ...this.store.cellPropsMap[rowKey]?.[colKey] };
        },
        // 对行设置的属性都转为对单元格设置属性
        setRowProps: (rowKey, props) => {
            this.store.leafColumnKeys.forEach(colKey => {
                this.setCellProps(rowKey, colKey, props);
            });
        },
        clearCellProps: () => {
            this.store.cellPropsMap = {};
        },
        // 应该没有使用场景，除了status（status单独抽出来了，在下面）
        getRowProps: () => {
            
        },
        setRowStatus: (rowKey, status) => {
            let row = this.getRowData(rowKey);
            row.status = status;
            this.setRowData(row);
        },
        getRowStatus: rowKey => {
            return this.getRowData(rowKey).status;
        },
        setActiveCell: ({ colKey, rowKey }) => {
            let prevActiveCell = this.store.activeCell || {};
            this.store.activeCell = { colKey, rowKey };
            if (prevActiveCell.rowKey !== rowKey || prevActiveCell.colKey !== colKey) {
                // this.focusOnCell(rowKey, colKey);
                
                let itemId = this.itemIDManager.getID({ colKey, rowKey }),
                    prevId = this.itemIDManager.getID(prevActiveCell);
                
                // blur为了触发前一个Item的编辑后
                prevId && this.blurComponent(prevId);
                prevId && this.updateComponent(prevId);
                itemId && this.updateComponent(itemId);
            }
        },
        getActiveCell: () => {
            return this.store.activeCell || {};
        },
        validate: customerFunc => {
            let errInfo = new Map(),
                rows = this.getRows().filter(row => row.status !== ROW_STATUS.DELETE), //filter row
                columns = this.getVisibleLeafColumnKeys().filter(colKey => this.getColumnProps(colKey, 'required'));

            // 2. 所有可见数据对每一列验证数据
            rows.forEach(row => {
                let rowErrorLabel = new Set();
                // let rowToast = '第'+ (row.viewIndex + 1) + '行：';
                columns.forEach(colKey => {
                    let invalid = false;
                    if(customerFunc && typeof customerFunc === 'function'){
                        let validated = customerFunc({
                            required: this.getColumnProps(colKey, 'required'),
                            rowStatus: row.status,
                            attrcode: colKey,
                            values: row.values,
                            column: this.getColumnProps(),
                            rowKeyValue: row.key,
                            saga_status: row.values?.saga_status || {},
                        });

                        invalid = !validated.passed;
                    } else {
                        let cellValue = this.getCellValue(row.key, colKey);
                        invalid = [undefined, null, ''].includes(cellValue?.value);
                    }

                    invalid && rowErrorLabel.add(colKey);
                    this.setCellProps(row.key, colKey, { invalid });
                });
                if(rowErrorLabel.size){
                    errInfo.set(row.key, rowErrorLabel);
                }
            });

            // 3. 存储已被检查过的数据 TODO 还需要一个get接口
            this.store.validatedInfo = errInfo;

            if (errInfo.size) {
                // 为了更新CardTable页签的错误标识
                this.forceUpdate();
            }
            return errInfo;
        },
        clearValidate: rowKeys => {
            let validatedInfo = this.store.validatedInfo;

            if(!validatedInfo || !validatedInfo.size) return; //避免多余的update和循环

            if(rowKeys){ //根据rowKey清除验证
                !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

                rowKeys.forEach(rowKey => {
                    if (validatedInfo.has(rowKey)) {
                        validatedInfo.get(rowKey).forEach(colKey => {
                            this.setCellProps(rowKey, colKey, { invalid: false });
                        });
                    }
                    validatedInfo.delete(rowKey);
                });
            } else {
                validatedInfo.forEach((colKeys, rowKey) => 
                    colKeys.forEach(colKey => this.setCellProps(rowKey, colKey, { invalid: false })));

                validatedInfo = new Map();
            }

            this.store.validatedInfo = validatedInfo;
            this.forceUpdate();
        },
        clearCellValidate: (rowKey, colKey) => {
            let validatedInfo = this.getValidateInfo();
            if (validatedInfo.has(rowKey)) {
                let colKeys = validatedInfo.get(rowKey);
                colKeys.delete(colKey);
                if (!colKeys.size) {
                    // 为了清除CardTable页签的错误标识
                    validatedInfo.delete(rowKey);
                    this.forceUpdate();
                }
                this.store.validatedInfo = validatedInfo;
            }
        },
        getValidateInfo: () => {
            return this.store.validatedInfo;
        },
        filterEmptyRows: (keys = [], rule = "except", isDeepDel) => {
            let rows = this.getRows(),
                keySet = new Set(keys),
                columns = this.getColumns().filter(col => 
                    isValidType(this.getColumnProps(col.key, 'itemtype')) && 
                    col.key !== 'numberindex' &&
                    (rule === 'except' ? !keySet.has(col.key) : keySet.has(col.key)),
                );

            rows = rows.filter(row => {
                let isEmpty = columns.every(col => !row.values?.[col.key]?.value);

                if (isEmpty) {
                    if (!isDeepDel && row.status !== ROW_STATUS.ADD) {
                        row.status = ROW_STATUS.DELETE;
                        return true;
                    }
                    return false;
                }
                return true;
            });

            return rows;
        },
    };
};
useStore.propsGenerator = function(props) {
    let { store } = props,
        { getInstance, getVisibleRows, getVisibleColumns, getAllRowsInfo, getStatus } = store;

    let { itemIDManager } = getInstance(),
        status = getStatus(),
        globalTableConfig = JSON.parse(getGlobalStorage('localStorage', 'nccTable')) || {},
        ncc_automated = getGlobalStorage("localStorage", "_ncc_automated_"),
        hoverContent = true,
        hasOprColumn = false;

    if (ncc_automated === '1' || globalTableConfig.rowcheckedButton !== 'suspension') {
        hoverContent = false;
    }

    let rows = getVisibleRows(),
        columns = dfs(getVisibleColumns(), column => {
            column = { ...column };
            let { attrcode, itemtype, visible, render } = column;

            if (
                hoverContent && 
                (attrcode === "opr" || attrcode === 'operate') &&
                itemtype === "customer" &&
                render && 
                visible
            ) {
                hasOprColumn = true;
                // 操作列模式切换
                let _title = column.title;
                column.width = 1;
                column.title = <div style={{ display: 'none' }}>{_title}</div>;
                column.resizable = false;
            } 

            column.render = row => {
                let rowKey = row.key,
                    itemId = itemIDManager.getID({ rowKey, colKey: attrcode });
                return <Cell 
                    key={itemId} 
                    rowKey={rowKey} 
                    colKey={attrcode} 
                    store={store} 
                    hoverContent={hoverContent}
                />;
            };

            return column;
        }),
        allRowsInfo = getAllRowsInfo();

    let hasSuspendedRow = hoverContent && hasOprColumn;

    return {
        ...props,
        columns,
        rows,
        allRowsInfo,
        className: classNames(props.className, `table-status-${status}`, { 'suspended-row': hasSuspendedRow }),
        locale: store.getLocale(),
        hasSuspendedRow,
    };
};